import type { StoredJoinSplitNote } from "../../joinsplit/types";
import type { SolanaNetworkConfig } from "../../config/solana";
import { selectForAmount } from "../spendSelection";
import { isSolanaFlatNote, solanaNoteMint } from "./notes";
import { readSolanaPoolConfig } from "./sync";
import type { SolanaRpc } from "./rpc";
import type { SolanaRelayClient } from "./relay";
import {
  validateSolanaFeeQuote,
  type SolanaFeeRequest,
  type SolanaWithdrawalQuote,
} from "./fees";

export interface SolanaQuoteContext {
  rpc: SolanaRpc;
  relay: SolanaRelayClient;
  network: SolanaNetworkConfig;
  notes: StoredJoinSplitNote[];
  mint: string | null;
  recipients: string[];
  signal?: AbortSignal;
}

async function quoteContext(
  ctx: SolanaQuoteContext,
  kind: SolanaFeeRequest["kind"],
) {
  const { poolConfig } = await readSolanaPoolConfig(ctx.rpc, ctx.network);
  const seen = new Set<string>();
  const notes = ctx.notes
    .filter(({ note, spent }) => {
      if (
        spent ||
        !isSolanaFlatNote(note, ctx.network) ||
        note.treeIndex !== poolConfig.currentTreeIndex ||
        note.leafIndex < 0 ||
        note.value <= 0n
      )
        return false;
      try {
        if (solanaNoteMint(note) !== ctx.mint) return false;
      } catch {
        return false;
      }
      const key = note.commitment.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) =>
      a.note.value === b.note.value
        ? a.note.leafIndex - b.note.leafIndex
        : a.note.value > b.note.value
          ? -1
          : 1,
    )
    .slice(0, 32);
  const quote = async (
    selected: StoredJoinSplitNote[],
    outputCount: number,
    amount: bigint,
  ): Promise<SolanaWithdrawalQuote> => {
    if (ctx.signal?.aborted) throw new Error("Fee quote cancelled");
    const request: SolanaFeeRequest = {
      network: ctx.network.key,
      programId: ctx.network.programId,
      kind,
      mint: ctx.mint,
      treeIndex: poolConfig.currentTreeIndex,
      inputCount: selected.length,
      outputCount,
      recipients: ctx.recipients,
    };
    const response = validateSolanaFeeQuote(
      await ctx.relay.quote(request, { signal: ctx.signal }),
      request,
    );
    if (response.feeRecipient !== poolConfig.feeRecipient)
      throw new Error("Relayer fee recipient does not match this pool.");
    return {
      quote: response,
      amount,
      inputTotal: selected.reduce((sum, { note }) => sum + note.value, 0n),
      selectedCommitments: [...selected]
        .sort((a, b) => a.note.leafIndex - b.note.leafIndex)
        .map(({ note }) => note.commitment),
    };
  };
  return { notes, quote };
}

export async function quoteSolanaWithdrawal(
  ctx: SolanaQuoteContext,
  amount: bigint,
  batch: boolean,
): Promise<SolanaWithdrawalQuote> {
  if (amount <= 0n || amount >= 1n << 64n)
    throw new Error("Enter a valid withdrawal amount.");
  const { notes, quote } = await quoteContext(
    ctx,
    batch ? "withdraw_batch" : "withdraw",
  );
  if (!batch) {
    const selected = selectForAmount(notes, amount);
    const total = selected.reduce((sum, { note }) => sum + note.value, 0n);
    return quote(selected, total > amount ? 1 : 0, amount);
  }
  // Adding the fee can require another input, which itself has rent. Evaluate
  // each funded prefix and the exact-spend boundary; never oscillate between
  // zero change and change or silently substitute the old fixed fee.
  let total = 0n;
  for (let count = 1; count <= notes.length; count++) {
    total += notes[count - 1].note.value;
    if (total <= amount) continue;
    const selected = notes.slice(0, count);
    const exact = await quote(selected, 0, amount);
    if (total === amount + BigInt(exact.quote.fee)) return exact;
    if (total < amount + BigInt(exact.quote.fee)) continue;
    const change = await quote(selected, 1, amount);
    if (total > amount + BigInt(change.quote.fee)) return change;
  }
  throw new Error(
    "Insufficient private balance for the recipient amounts and live withdrawal fee. Please deposit more funds.",
  );
}

export async function maxSolanaBatchPayout(
  ctx: SolanaQuoteContext,
): Promise<bigint> {
  const { notes, quote } = await quoteContext(ctx, "withdraw_batch");
  if (!notes.length) return 0n;
  const total = notes.reduce((sum, { note }) => sum + note.value, 0n);
  const priced = await quote(notes, 0, total);
  const fee = BigInt(priced.quote.fee);
  // The on-chain batch action also requires fee <= recipient total.
  return total >= 2n * fee && total < 1n << 64n ? total - fee : 0n;
}
